<script lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogTitle,
    DialogDescription,
  } from "$lib/components/ui/dialog";  
  import { Button } from "$lib/components/ui/button";

  import PerforationEditor from "./PerforationEditor.svelte";
  import PictureEditor from "./PictureEditor.svelte";
  import Picture from "../Picture.svelte";
  import FaceValueEditor from "./FaceValueEditor.svelte";
  import DateEditor from "./DateEditor.svelte";
  import TitleEditor from "./TitleEditor.svelte";
  import DescriptionEditor from "./DescriptionEditor.svelte";

  import GumTypeSelect from './GumTypeSelect.svelte';
  import ColorEditor from './ColorEditor.svelte';
  import { StampEditorModel } from "./models/StampEditorModel.svelte"
  import PrintRunEditor from "./PrintRunEditor.svelte";
  import CatalogIdsEditor from "./CatalogIdsEditor.svelte";
  import ZhihaoEditor from "./ZhihaoEditor.svelte";
  import LabelsEditor from "./LabelsEditor.svelte";
  import { getLabelsForType } from "$lib/philatelic-labels";
  import { PhilatelicItemType } from "@briefmarken_katalog/types";
  import { t } from "$lib/i18n";
  import { createBindableState, type BindableValue } from './utils.svelte';
  import HelpButton from "../HelpButton.svelte";

  interface Props {
    isOpen: BindableValue<boolean>;
    model: StampEditorModel;
  }

  let { isOpen = $bindable(false), model = $bindable() }: Props = $props();

  let open = createBindableState(
    () => isOpen,
    (v) => isOpen = v,
    false
  );

</script>

<Dialog bind:open={open.current}>
  <DialogContent class="sm:max-w-2xl flex flex-col overflow-hidden max-h-[90dvh]">
    <div class="flex items-center gap-2">
      <DialogTitle>{$t(model.dialogTitle)}</DialogTitle>
      <HelpButton sectionId="stamp-editor" />
    </div>
    <DialogDescription>{$t(model.dialogSubtitle)}{model.dialogSubtitleSuffix}</DialogDescription>
    <div class="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
      <TitleEditor isReadonly={model.isReadonly} bind:title={model.title} invalid={!model.isReadonly && model.isTitleInvalid}/>
      <DescriptionEditor isReadonly={model.isReadonly} bind:description={model.description}/>
      <DateEditor isReadonly={model.isReadonly} bind:date={model.date_of_issue} bind:expiryDate={model.expiry_date} invalid={!model.isReadonly && model.isDateInvalid}/>
      <FaceValueEditor
        isReadonly={model.isReadonly}
        bind:value={model.face_value_value}
        bind:unit={model.face_value_unit}
        invalidAmount={!model.isReadonly && model.isFaceValueAmountInvalid}
        invalidUnit={!model.isReadonly && model.isFaceValueUnitInvalid}
      />
{#if model.picture}
      <Picture picture={model.picture}/>
{/if}
{#if !model.isReadonly}
      <PictureEditor
        isReadonly={model.isReadonly}
        hasPicture={!!model.picture}
        handleImageUpload={model.handleImageUpload.bind(model)}
        onPhoneUpload={model.setPictureFromPhone.bind(model)}
        />
{/if}
      <PerforationEditor
        isReadonly={model.isReadonly}
        bind:horizontal={model.perforation_count_horizontal}
        bind:vertical={model.perforation_count_vertical}
        bind:type={model.perforation_type}
        bind:security={model.security_perforation}
      />
      <div class="grid grid-cols-4 items-center gap-4">
        <GumTypeSelect bind:value={model.with_gum} isReadonly={model.isReadonly} containerClass="contents" selectClass="col-span-1" />
        <PrintRunEditor isReadonly={model.isReadonly} bind:printRun={model.print_run} containerClass="contents" inputClass="col-span-1" labelClass="justify-end" />
      </div>
      <ColorEditor bind:value={model.color} isReadonly={model.isReadonly} />
      <div class="flex gap-2">
        <ZhihaoEditor isReadonly={model.isReadonly} bind:zhihao={model.zhihao} dateOfIssue={model.date_of_issue} setNotation="omitted" containerClass="contents" />
        <CatalogIdsEditor isReadonly={model.isReadonly} bind:catalogIds={model.catalog_ids} containerClass="contents" />
        <LabelsEditor isReadonly={model.isReadonly} bind:labels={model.categories} availableLabels={getLabelsForType(PhilatelicItemType.SingleStamp)} containerClass="contents" />
      </div>
    </div>

  {#if model.showError}
    <div class="p-4 text-sm text-red-500 bg-red-50 rounded-md">
      {model.error ? $t(model.error) : ''}
    </div>
  {/if}

  {#if model.isReview}
    <DialogFooter>
      {#if model.isDirty}
        <form onsubmit={(ev: Event) => { model.onsubmit(() => { open.current = false; }, ev) }}>
          <Button type="submit" disabled={!model.isValid}>{$t('editor.save')}</Button>
        </form>
      {/if}
      <Button onclick={() => model.onreview('Accepted', () => { open.current = false; })}>{$t('editor.accept')}</Button>
      <Button onclick={() => model.onreview('Rejected', () => { open.current = false; })}>{$t('editor.reject')}</Button>
    </DialogFooter>
  {:else if model.isDelete}
    <div class="p-4 text-lg font-bold text-red-600 bg-red-50 rounded-md text-center">
      {$t('deleteSuggestion.warning')}
    </div>
    <DialogFooter>
      <Button variant="outline" onclick={() => open.current = false}>{$t('deleteSuggestion.cancel')}</Button>
      <Button variant="destructive" onclick={() => model.ondelete(() => { open.current = false; })}>{$t('deleteSuggestion.confirm')}</Button>
    </DialogFooter>
  {:else if model.showSubmitButton}
    <form onsubmit={(ev: Event) => { model.onsubmit(()=>{open.current = false; }, ev) }}>
      <DialogFooter>
        <Button type="submit" disabled={!model.isDirty || !model.isValid}>{$t(model.isEdit ? 'editor.save' : 'editor.addStampSuggestion')}</Button>
      </DialogFooter>
    </form>
  {/if}
  </DialogContent>
</Dialog>
